import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BaseWrapper from "../../components/shared/BaseWrapper";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import {
  Box,
  Button,
  Typography,
  Grid,
  Paper,
  Stack,
  CardHeader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert,
  TextField,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { deleteInvoiceItem } from "../API/billingAPI";

const formatDisplayDateTime = (value) => {
  if (!value) return 'N/A';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  const [date] = value.split('T');

  const time = d.toLocaleTimeString('en-PH', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Manila',
  });

  return `${date} ${time}`;
};

function DeleteInvoice() {
  const navigate = useNavigate();
  const location = useLocation();
  const invoice = location.state?.invoice;
  const selectedClient = location.state?.selectedClient;
  const selectedContract = location.state?.selectedContract;
  const source = location.state?.source || 'manageContracts';
  const contractSource = location.state?.contractSource;
  const contractId = selectedContract?.contract_id || selectedContract?.id;
  const CURRENCY = invoice?.currency || 'PHP';
  const [ deleteLoading, setDeleteLoading ] = useState(false);
  const [ deleteInput, setDeleteInput ] = useState("");
  const [ deleteDialogOpen, setDeleteDialogOpen ] = useState(false);

  const backRoute =
    source === 'manageClients' ? '/manage-clients' :
    source === 'viewContract' ? `/view-contract/${contractId}` :
    '/manage-contracts';

  const backState = useMemo(() =>
    source === 'viewContract'
      ? { contract: selectedContract, selectedClient, source: contractSource }
      : { selectedClient },
    [source, selectedContract, selectedClient, contractSource]
  );

  const isDeleteConfirmed = deleteInput === "DELETE";
  const isOpenInvoice = invoice?.status === 'OPEN';
  const isPaidInvoice = invoice?.status === 'PAID';

  const handleBackClick = () => {
    navigate(backRoute, { state: backState });
  };

  const handleDeleteConfirm = async () => {
    if (!invoice || !isDeleteConfirmed) return;

    try {
      setDeleteLoading(true);

      const res = await deleteInvoiceItem(
        invoice?.contract_id || selectedContract?.contract_id || selectedContract?.id || invoice?.id,
        invoice?.billing_date,
        invoice?.invoice_id,
        isOpenInvoice,
      );

      if (res.success) {
        setDeleteInput("");
        setDeleteDialogOpen(false);
        navigate(backRoute, { state: backState });
      } else {
        alert("Failed to delete invoice.");
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      alert("Failed to delete invoice.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDialogClose = () => {
    if (!deleteLoading) {
      setDeleteInput("");
      setDeleteDialogOpen(false);
    }
  };

  if (!invoice) {
    return (
      <BaseWrapper
        titleIcon={<ReceiptLongIcon />}
        title="Billing"
        subTitle="Delete Invoice"
        subTitleRoute="/delete-invoice"
      >
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            No invoice data available
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
          >
            Back to Invoices
          </Button>
        </Box>
      </BaseWrapper>
    );
  }

  return (
    <BaseWrapper
      titleIcon={<ReceiptLongIcon />}
      title="Billing"
      subTitle="Delete Invoice"
      subTitleRoute="/delete-invoice"
    >
      <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
            Invoice Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Invoice Number: {invoice?.invoice_number || 'N/A'}
          </Typography>
        </Box>

        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          <Button
            variant="contained"
            color="error"
            onClick={() => setDeleteDialogOpen(true)}
          >
            Delete
          </Button>

          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
          >
            Back
          </Button>
        </Stack>

        <Box sx={{ mb: 3 }}>
          <CardHeader
            title={
              <Typography
                variant="h6"
                sx={{
                  paddingBottom: '0.5rem',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  fontSize: '1rem',
                }}
              >
                Basic Information
              </Typography>
            }
            sx={{ pb: 0, pt: 0 }}
          />
        </Box>

        <Paper elevation={0} sx={{ p: 3, backgroundColor: '#f9f9f9', borderRadius: 2, mb: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>
                  Invoice Number
                </Typography>
                <Typography variant="body1">{invoice?.invoice_number || 'N/A'}</Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>
                  Contract ID
                </Typography>
                <Typography variant="body1">{invoice?.contract_id || selectedContract?.contract_id || 'N/A'}</Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>
                  Billing Date
                </Typography>
                <Typography variant="body1">{invoice?.billing_date || 'N/A'}</Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>
                  Due Date
                </Typography>
                <Typography variant="body1">{invoice?.due_date || 'N/A'}</Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>
                  Total Due
                </Typography>
                <Typography variant="body1">
                  {CURRENCY} {((invoice?.total_due_minor ?? 0) / 100).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>
                  Status
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    display: 'inline-block',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    backgroundColor: invoice?.status === 'PAID' ? '#d4edda' : invoice?.status === 'OVERDUE' ? '#f8d7da' : '#fff9c4',
                    color: invoice?.status === 'PAID' ? '#155724' : invoice?.status === 'OVERDUE' ? '#721c24' : '#978d2d',
                    fontWeight: 'bold',
                  }}
                >
                  {invoice?.status || 'N/A'}
                </Typography>
              </Box>
            </Grid>

            {isPaidInvoice && (
              <>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>
                      Payment Date
                    </Typography>
                    <Typography variant="body1">{invoice?.payment_date || 'N/A'}</Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>
                      Payment Notes
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {invoice?.payment_notes || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
        </Paper>

        <Box sx={{ mb: 3 }}>
          <CardHeader
            title={
              <Typography
                variant="h6"
                sx={{
                  paddingBottom: '0.5rem',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  fontSize: '1rem',
                }}
              >
                Metadata
              </Typography>
            }
            sx={{ pb: 0, pt: 0 }}
          />
        </Box>

        <Paper elevation={0} sx={{ p: 3, backgroundColor: '#f9f9f9', borderRadius: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>
                  Created On
                </Typography>
                <Typography variant="body2">{formatDisplayDateTime(invoice?.timestamp_create)}</Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>
                  Created By
                </Typography>
                <Typography variant="body2">{invoice?.user_create?.name || invoice?.user_create || 'N/A'}</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Invoice</DialogTitle>

        <DialogContent>
          <Alert severity={isOpenInvoice ? "warning" : "error"} sx={{ mb: 2 }}>
            {isOpenInvoice
              ? "This is an open invoice. Deleting it may impact billing records."
              : "This action cannot be undone."}
          </Alert>

          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to delete this invoice?

            <br /><br />

            Type <strong>DELETE</strong> to proceed.
          </DialogContentText>

          <TextField
            autoFocus
            fullWidth
            label="Type DELETE to confirm"
            value={deleteInput}
            onChange={(e) => setDeleteInput(e.target.value)}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleDialogClose} disabled={deleteLoading}>
            Cancel
          </Button>

          <Button
            color="error"
            variant="contained"
            disabled={!isDeleteConfirmed || deleteLoading}
            onClick={handleDeleteConfirm}
          >
            {deleteLoading
              ? <CircularProgress size={20} color="inherit" />
              : "Delete Invoice"}
          </Button>
        </DialogActions>
      </Dialog>
    </BaseWrapper>
  );
}

export default DeleteInvoice;
